import styled from 'styled-components';
import {Button, EMPHASIS} from '@forecast-it/design-system';
import {LabelledButton} from './LabelledButton';
import {EmptyState} from './EmptyState';
import {graphql, useFragment} from 'react-relay';
import type {RelatedTab_ticket$key} from './__generated__/RelatedTab_ticket.graphql';
import type {ticketingPerson_person$key} from '../../../graphql/fragments/__generated__/ticketingPerson_person.graphql';
import {ticketingPersonFragment, personDisplayName} from '../../../graphql/fragments/ticketingPerson';
import {TicketStatusBadge} from './TicketStatusBadge';
import {useDeleteTicketLink} from '../hooks/useDeleteTicketLink';
import {useAuth} from '../../auth/context/AuthContext';
import {hasTicketPerm, TICKET_PERMS} from '../../auth/utils/ticketPerms';
import type {TicketStatus} from '../types/ticket';

type AvatarVariant = 'assignee' | 'creator';
const AVATAR_COLORS: Record<AvatarVariant, {bg: string; text: string}> = {
	assignee: {bg: '#dbeafe', text: '#1d4ed8'}, // blue-100 / blue-700
	creator: {bg: '#f3e8ff', text: '#7e22ce'}, // purple-100 / purple-700
};

const RelatedTabFragment = graphql`
	fragment RelatedTab_ticket on Ticket {
		id
		links {
			id
			linkType
			createdAt
			targetTicket {
				id
				number
				subject
				status
				assignee {
					...ticketingPerson_person
				}
			}
			targetProject {
				id
				name
				color
			}
			creator {
				...ticketingPerson_person
			}
		}
	}
`;

interface Props {
	ticket: RelatedTab_ticket$key;
	onAddLink?: () => void;
	onRefetch?: () => void;
}

const PersonWrap = styled.span`
	display: inline-flex;
	align-items: center;
	gap: 6px;
`;

const PersonAvatar = styled.span<{$variant: AvatarVariant}>`
	display: flex;
	height: 24px;
	width: 24px;
	align-items: center;
	justify-content: center;
	border-radius: 9999px;
	${p => p.theme.sys.font?.detail?.['1']}
	font-weight: 500;
	background: ${p => AVATAR_COLORS[p.$variant].bg};
	color: ${p => AVATAR_COLORS[p.$variant].text};
`;

/** Renders a person avatar-initial + display name for a link's assignee/creator. */
function PersonInline({person, variant}: {person: ticketingPerson_person$key | null | undefined; variant: AvatarVariant}) {
	const data = useFragment(ticketingPersonFragment, person ?? null);
	const name = personDisplayName(data);
	return (
		<PersonWrap>
			<PersonAvatar $variant={variant}>{name.charAt(0).toUpperCase()}</PersonAvatar>
			{name}
		</PersonWrap>
	);
}

const GROUP_LABELS: Record<string, string> = {
	related: 'Tickets',
	child: 'Child tickets',
	project: 'Projects',
};

const GROUP_ORDER = ['related', 'child', 'project'] as const;

const Stack = styled.div`
	> * + * {
		margin-top: 24px;
	}
`;

const EmptyCard = EmptyState;

const EmptyIcon = styled.svg`
	margin: 0 auto;
	height: 40px;
	width: 40px;
	color: #cbd5e1;
`;

const EmptyTitle = styled.p`
	margin: 12px 0 0;
	${p => p.theme.sys.font?.subheading?.['2']}
	font-weight: 500;
	color: #0f172a;
`;

const EmptyHint = styled.p`
	margin: 4px 0 0;
	${p => p.theme.sys.font?.subheading?.['2']}
	color: #64748b;
`;

const SectionHead = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
`;

const SectionTitleGroup = styled.div`
	display: flex;
	align-items: baseline;
	gap: 8px;
`;

const SectionTitle = styled.h3`
	margin: 0;
	${p => p.theme.sys.font?.subheading?.['1']}
	letter-spacing: 0.15px;
	color: #0f172a;
`;

const SectionCount = styled.span`
	${p => p.theme.sys.font?.detail?.['1']}
	color: #94a3b8;
`;

const SectionHint = styled.p`
	margin: 4px 0 0;
	${p => p.theme.sys.font?.subheading?.['2']}
	color: #64748b;
`;

const GroupTitle = styled.h4`
	margin: 0 0 12px;
	${p => p.theme.sys.font?.subheading?.['2']}
	font-weight: 600;
	color: #0f172a;
`;

const TableCard = styled.div`
	overflow: hidden;
	border-radius: 8px;
	border: 1px solid ${p => p.theme.sys.color.border.decorative};
	background: #ffffff;
	box-shadow: 0 1px 2px rgb(0 0 0 / 0.05);
`;

const Table = styled.table`
	min-width: 100%;
	border-collapse: collapse;
`;

const Thead = styled.thead`
	background: #f8fafc;
`;

const Th = styled.th<{$right?: boolean}>`
	padding: 10px 16px;
	text-align: ${p => (p.$right ? 'right' : 'left')};
	${p => p.theme.sys.font?.tags?.['1']}
	font-weight: 500;
	text-transform: uppercase;
	letter-spacing: 0.025em;
	color: #64748b;
	border-bottom: 1px solid ${p => p.theme.sys.color.border.decorative};
`;

const Tr = styled.tr`
	border-top: 1px solid #f1f5f9;
	&:hover {
		background: #f8fafc;
	}
`;

const Td = styled.td<{$nowrap?: boolean; $right?: boolean}>`
	padding: 12px 16px;
	${p => p.theme.sys.font?.subheading?.['2']}
	color: #0f172a;
	${p => p.$nowrap && 'white-space: nowrap;'}
	${p => p.$right && 'text-align: right;'}
`;

const IdCell = styled(Td)`
	/* The font shorthand resets weight to the token's 400, so the override must follow it. */
	${p => p.theme.sys.font?.tags?.['1']}
	font-weight: 500;
	color: #334155;
`;

const AssigneeCell = styled(Td)`
	color: #475569;
`;

const ProjectTag = styled.span`
	display: inline-flex;
	align-items: center;
	gap: 6px;
	border-radius: 9999px;
	background: #fef9c3;
	padding: 2px 10px;
	${p => p.theme.sys.font?.detail?.['1']}
	font-weight: 500;
	color: #854d0e;
`;

const ProjectDot = styled.span`
	height: 6px;
	width: 6px;
	border-radius: 9999px;
	background: #eab308;
`;

const Muted = styled.span`
	color: #94a3b8;
`;

const RemoveIcon = styled.svg`
	height: 16px;
	width: 16px;
`;

export function RelatedTab({ticket: ticketRef, onAddLink, onRefetch}: Props) {
	const data = useFragment(RelatedTabFragment, ticketRef);
	const {user} = useAuth();
	const {submit: deleteLink, loading: deleting} = useDeleteTicketLink();
	const canManage = !!user && hasTicketPerm(user.ticketPerms, TICKET_PERMS.MANAGE);

	const handleDelete = (linkId: string) => {
		deleteLink(linkId).then(
			() => onRefetch?.(),
			() => {}
		);
	};

	const grouped = GROUP_ORDER.map(type => ({
		type,
		label: GROUP_LABELS[type],
		links: data.links.filter(l => l.linkType === type),
	})).filter(g => g.links.length > 0);

	const totalCount = data.links.length;

	if (grouped.length === 0) {
		return (
			<EmptyCard>
				<EmptyIcon fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m9.86-2.02a4.5 4.5 0 0 0-1.242-7.244l4.5-4.5a4.5 4.5 0 0 1 6.364 6.364l-1.757 1.757"
					/>
				</EmptyIcon>
				<EmptyTitle>No linked items yet</EmptyTitle>
				<EmptyHint>Link related tickets or projects to keep track of dependencies.</EmptyHint>
				{onAddLink && (
					<Button emphasis={EMPHASIS.HIGH} onPress={() => onAddLink?.()}>
						+ Add link
					</Button>
				)}
			</EmptyCard>
		);
	}

	return (
		<Stack>
			<div>
				<SectionHead>
					<SectionTitleGroup>
						<SectionTitle>Related items</SectionTitle>
						<SectionCount>
							{totalCount} {totalCount === 1 ? 'item' : 'items'} associated with this requester
						</SectionCount>
					</SectionTitleGroup>
					{onAddLink && (
						<Button emphasis={EMPHASIS.HIGH} onPress={() => onAddLink?.()}>
							+ Add link
						</Button>
					)}
				</SectionHead>
				<SectionHint>
					Other open tickets and projects tied to this requester. Click through to see context for the broader
					engagement.
				</SectionHint>
			</div>

			{grouped.map(group => (
				<div key={group.type}>
					<GroupTitle>
						{group.label} ({group.links.length})
					</GroupTitle>
					<TableCard>
						<Table>
							<Thead>
								<tr>
									<Th>ID</Th>
									<Th>{group.type === 'project' ? 'Title' : 'Subject'}</Th>
									<Th>Status</Th>
									<Th>{group.type === 'project' ? 'Owner' : 'Assignee'}</Th>
									<Th $right>{/* actions */}</Th>
								</tr>
							</Thead>
							<tbody>
								{group.links.map(link => (
									<Tr key={link.id}>
										<IdCell $nowrap>
											{link.targetTicket
												? `TK-${link.targetTicket.number}`
												: link.targetProject?.id ?? '—'}
										</IdCell>
										<Td>{link.targetTicket?.subject ?? link.targetProject?.name ?? '—'}</Td>
										<Td $nowrap>
											{group.type !== 'project' && link.targetTicket?.status && (
												<TicketStatusBadge status={link.targetTicket.status as TicketStatus} />
											)}
											{group.type === 'project' && link.targetProject && (
												<ProjectTag>
													<ProjectDot
														aria-hidden="true"
														style={
															link.targetProject.color
																? {background: link.targetProject.color}
																: undefined
														}
													/>
													Project
												</ProjectTag>
											)}
										</Td>
										<AssigneeCell $nowrap>
											{group.type !== 'project' && (
												<>
													{link.targetTicket?.assignee ? (
														<PersonInline person={link.targetTicket.assignee} variant="assignee" />
													) : (
														<Muted>Unassigned</Muted>
													)}
												</>
											)}
											{group.type === 'project' && (
												<>
													{link.creator ? (
														<PersonInline person={link.creator} variant="creator" />
													) : (
														<Muted>—</Muted>
													)}
												</>
											)}
										</AssigneeCell>
										<Td $nowrap $right>
											{canManage && (
												<LabelledButton
													emphasis={EMPHASIS.LOW}
													label={`Remove link to ${
														link.targetTicket
															? `TK-${link.targetTicket.number}`
															: link.targetProject?.name ?? 'item'
													}`}
													disabled={deleting}
													onPress={() => handleDelete(link.id)}
												>
													<RemoveIcon
														fill="none"
														viewBox="0 0 24 24"
														stroke="currentColor"
														strokeWidth={2}
													>
														<path
															strokeLinecap="round"
															strokeLinejoin="round"
															d="M6 18 18 6M6 6l12 12"
														/>
													</RemoveIcon>
												</LabelledButton>
											)}
										</Td>
									</Tr>
								))}
							</tbody>
						</Table>
					</TableCard>
				</div>
			))}
		</Stack>
	);
}
